/**
 * <nb-slider> — arrows and dots layered over a native scroll-snap track.
 *
 * The track scrolls and snaps on its own, so swipe, trackpad and keyboard
 * scrolling all work before this script runs. This class only adds the arrow
 * buttons, the dot indicators, and wrap-around paging.
 */
class NbSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.nb-slider__track');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('.nb-slider__slide'));
    if (this.slides.length < 2) return;

    this.dots = Array.from(this.querySelectorAll('.nb-slider__dot'));
    this.index = 0;

    this.querySelector('.nb-slider__btn--prev')?.addEventListener('click', this.onPrev);
    this.querySelector('.nb-slider__btn--next')?.addEventListener('click', this.onNext);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    // Opt-in autoplay: set autoplay="<ms>" on the element. Off entirely for
    // prefers-reduced-motion, and paused while a pointer or keyboard focus is
    // on the slider so it never fights the visitor's own interaction.
    const delay = parseInt(this.getAttribute('autoplay'), 10);
    if (delay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.autoplayDelay = delay;
      this.addEventListener('pointerenter', this.stopAutoplay);
      this.addEventListener('pointerleave', this.startAutoplay);
      this.addEventListener('focusin', this.stopAutoplay);
      this.addEventListener('focusout', this.startAutoplay);
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    cancelAnimationFrame(this.raf);
    this.stopAutoplay();
  }

  startAutoplay = () => {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => this.go(this.index + 1), this.autoplayDelay);
  };

  stopAutoplay = () => {
    clearInterval(this.autoplayTimer);
  };

  /* The buttons sit inside a card whose title carries a stretched link, so a
     click here must not bubble up and navigate the page. */
  onPrev = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.go(this.index - 1);
  };

  onNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.go(this.index + 1);
  };

  /** Wraps at both ends, so neither arrow is ever a dead end. */
  go(target) {
    const count = this.slides.length;
    const next = (target + count) % count;
    this.index = next;
    this.track.scrollTo({
      left: this.slides[next].offsetLeft - this.track.offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    this.sync();
  }

  /* Scroll fires continuously during a swipe; derive the index on an animation
     frame rather than on every event. */
  onScroll = () => {
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => {
      const width = this.track.clientWidth || 1;
      const next = Math.round(this.track.scrollLeft / width);
      if (next === this.index) return;
      this.index = next;
      this.sync();
    });
  };

  sync() {
    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
    });
    this.slides.forEach((slide, i) => {
      // Keep off-screen slides out of the tab order and the accessibility tree.
      slide.toggleAttribute('inert', i !== this.index);
    });
  }
}

if (!customElements.get('nb-slider')) {
  customElements.define('nb-slider', NbSlider);
}
